import { Badge, Button, Surface } from '@chopchop-se/ui';
import type { ProductionTaskListItem, ProductionTaskStatus } from '@warehouse/shared';
import { CalendarClock, User } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { PRODUCTION_TASK_KIND_TONES, TASK_COLUMNS, isOverdue } from '../lib/status';
import { formatDateTime } from '../../../lib/format';

/**
 * The task board (spec §5.9 "tasks for when to unload goods").
 *
 * Presentational on purpose: it takes tasks and callbacks and owns no data
 * fetching, which is what makes the column split, the overdue flagging and the
 * per-status action set testable without a server.
 *
 * Columns are statuses rather than a drag surface: the board is read on a
 * warehouse tablet and glanced at from three metres away, and every column
 * change is a command with an audit entry — a drag is a worse way to express
 * that than a button that says what it does.
 */
export interface TaskBoardProps {
  tasks: readonly ProductionTaskListItem[];
  canManage: boolean;
  busyTaskId?: string | null;
  onStart: (task: ProductionTaskListItem) => void;
  onComplete: (task: ProductionTaskListItem) => void;
  onReopen: (task: ProductionTaskListItem) => void;
  onAssignToMe?: ((task: ProductionTaskListItem) => void) | undefined;
  emptyLabel: string;
}

export function TaskBoard({
  tasks,
  canManage,
  busyTaskId,
  onStart,
  onComplete,
  onReopen,
  onAssignToMe,
  emptyLabel,
}: TaskBoardProps): ReactNode {
  const { t } = useTranslation('production');

  const byStatus = new Map<ProductionTaskStatus, ProductionTaskListItem[]>(
    TASK_COLUMNS.map((status) => [status, []]),
  );
  for (const task of tasks) byStatus.get(task.status)?.push(task);

  return (
    <div className="grid gap-4 md:grid-cols-3">
      {TASK_COLUMNS.map((status) => {
        const column = byStatus.get(status) ?? [];
        return (
          <section key={status} aria-label={t(`tasks.columns.${status}`)} className="flex flex-col gap-3">
            <header className="flex items-center justify-between px-1">
              <h2 className="font-mono text-[0.7rem] uppercase tracking-[0.14em] text-ink-muted">
                {t(`tasks.columns.${status}`)}
              </h2>
              <span className="text-xs text-ink-muted">{column.length}</span>
            </header>

            {column.length === 0 ? (
              <Surface className="p-4 text-sm text-ink-muted">{emptyLabel}</Surface>
            ) : (
              column.map((task) => (
                <Surface key={task.id} className="flex flex-col gap-2.5 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="min-w-0 flex-1 text-sm font-medium text-ink">{task.title}</h3>
                    <Badge tone={PRODUCTION_TASK_KIND_TONES[task.kind]} size="sm">
                      {t(`tasks.kind.${task.kind}`)}
                    </Badge>
                  </div>

                  {task.description ? (
                    <p className="whitespace-pre-line text-sm text-ink-muted">{task.description}</p>
                  ) : null}

                  {task.productionOrderId ? (
                    <Link
                      to={`/production/orders/${task.productionOrderId}`}
                      className="font-mono text-xs text-brand-600 hover:underline"
                    >
                      {task.productionOrderNo}
                    </Link>
                  ) : null}

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
                    <span className="inline-flex items-center gap-1">
                      <User className="size-3.5" aria-hidden />
                      {task.assignedUserName ?? task.assignedRole ?? t('tasks.fields.unassigned')}
                    </span>
                    {task.dueAt ? (
                      <span className="inline-flex items-center gap-1">
                        <CalendarClock className="size-3.5" aria-hidden />
                        {formatDateTime(task.dueAt)}
                      </span>
                    ) : null}
                    {isOverdue(task) ? (
                      <Badge tone="danger" size="sm">
                        {t('tasks.overdue')}
                      </Badge>
                    ) : null}
                  </div>

                  {canManage ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {task.status === 'open' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={busyTaskId === task.id}
                          onClick={() => {
                            onStart(task);
                          }}
                        >
                          {t('tasks.actions.start')}
                        </Button>
                      ) : null}
                      {task.status !== 'done' ? (
                        <Button
                          size="sm"
                          loading={busyTaskId === task.id}
                          onClick={() => {
                            onComplete(task);
                          }}
                        >
                          {t('tasks.actions.complete')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={busyTaskId === task.id}
                          onClick={() => {
                            onReopen(task);
                          }}
                        >
                          {t('tasks.actions.reopen')}
                        </Button>
                      )}
                      {onAssignToMe && task.status !== 'done' && !task.assignedUser ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            onAssignToMe(task);
                          }}
                        >
                          {t('tasks.actions.assignToMe')}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </Surface>
              ))
            )}
          </section>
        );
      })}
    </div>
  );
}
